import * as React from "react";
import { nanoid } from "nanoid";
import {
  atomFamily,
  selector,
  SetterOrUpdater,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { TComponentType } from "../utils/types/common/components.types";
import { TUrlPath } from "../utils/types/elements/XText.types";
import { USE_X_PATH } from "./useXPath";
import { EDITABLE_PATHS } from "../utils/constants/common/urlPaths";

export const USE_X_PAGE = atomFamily<TComponentType[], TUrlPath>({
  key: "USE_X_PAGE",
  default: [],
});

export type TUpdateXPageArgsWithPath = {
  path: TUrlPath;
  elements: TComponentType[];
};

export type TUpdateXPageArgs = TComponentType[] | TUpdateXPageArgsWithPath;

export const USE_X_PAGE_SELECTOR = selector<TUpdateXPageArgs>({
  key: "USE_X_PAGE_SELECTOR",

  get: (opts) => {
    const path = opts.get(USE_X_PATH);

    if (!path) return [];

    const elements = opts.get(USE_X_PAGE(path));

    return elements;
  },

  set: (opts, newValue: TUpdateXPageArgs) => {
    // IF THE CODE MANUALLY SET THE PATH WE UPDATE THE PATH INDIVIDUALLY
    const value = newValue as TUpdateXPageArgsWithPath;

    if (value.path) {
      if (EDITABLE_PATHS.includes(value.path)) {
        const payload = value.elements.map((el) => ({
          ...el,
          config: {
            index: nanoid(5),
            ...el.config,
          },
        }));

        return opts.set(USE_X_PAGE(value.path), payload);
      }
    }
    // IF THE PATH IS NOT PROVIDED WE UPDATE THE CURRENT PATH
    else {
      const path = opts.get(USE_X_PATH);

      if (!path) return;

      return opts.set(
        USE_X_PAGE(path),
        (newValue as TComponentType[]).map((el) => ({
          ...el,
          config: {
            index: nanoid(5),
            ...el.config,
          },
        }))
      );
    }
  },
});

export function useXPage(initData?: TComponentType[]) {
  const [page, setXPage] = useRecoilState(USE_X_PAGE_SELECTOR);

  React.useEffect(() => {
    if (initData) {
      setXPage(initData);
    }
  }, [initData]);

  const xPage = page as TComponentType[];

  return { xPage, setXPage };
}

export function useXPageUpdater(): SetterOrUpdater<TUpdateXPageArgs> {
  return useSetRecoilState(USE_X_PAGE_SELECTOR);
}

export function useXPageValue() {
  return useRecoilValue(USE_X_PAGE_SELECTOR) as TComponentType[];
}
